import React from "react";
import { FileText } from "lucide-react";

const BillLoader = () => {
  return (
    <div className="fixed inset-0 bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center items-center z-50">
      <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-sm w-full mx-4">
        {/* Chef cooking animation container */}
        <div className="relative h-64 mb-8 flex justify-center items-center">
          {/* Enhanced circular track with gradient */}
          <div className="absolute h-56 w-56 rounded-full bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200 opacity-20 animate-pulse-slow"></div>
          <div className="absolute h-52 w-52 rounded-full border-4 border-blue-400 border-dashed animate-spin-slow"></div>
          
          {/* Inner glow effect */}
          <div className="absolute h-48 w-48 rounded-full bg-blue-100 opacity-30"></div>
          
          {/* Chef hat */}
          <div className="absolute top-0 z-20">
            <div className="w-24 h-6 bg-white rounded-t-full border-2 border-gray-800"></div>
            <div className="w-16 h-14 bg-white rounded-t-xl mx-auto border-2 border-b-0 border-gray-800"></div>
          </div>
          
          {/* Chef face */}
          <div className="absolute top-14 z-10">
            <div className="w-20 h-20 bg-orange-100 rounded-full border-2 border-gray-800 flex flex-col justify-center items-center">
              {/* Eyes */}
              <div className="flex w-12 justify-between mt-1">
                <div className="w-2 h-2 rounded-full bg-gray-800"></div>
                <div className="w-2 h-2 rounded-full bg-gray-800"></div>
              </div>
              {/* Smile - animated */}
              <div className="w-8 h-4 border-b-2 border-gray-800 rounded-b-full mt-2 animate-smile"></div>
            </div>
          </div>
          
          {/* Paper/invoice in hands */}
          <div className="absolute top-28 z-10">
            {/* Chef hands */}
            <div className="flex justify-between w-28 relative">
              <div className="w-6 h-10 bg-orange-100 rounded-full border-2 border-gray-800 absolute left-0 -ml-1"></div>
              <div className="w-6 h-10 bg-orange-100 rounded-full border-2 border-gray-800 absolute right-0 -mr-1"></div>
            </div>
            
            {/* Invoice */}
            <div className="w-24 h-32 bg-white border-2 border-blue-500 rounded-lg relative z-0 flex flex-col justify-start items-center pt-4 mx-auto">
              {/* Document lines */}
              <div className="w-16 h-1 bg-gray-300 mb-2 rounded"></div>
              <div className="w-14 h-1 bg-gray-300 mb-2 rounded"></div>
              <div className="w-16 h-1 bg-gray-300 mb-2 rounded"></div>
              <div className="w-14 h-1 bg-gray-300 rounded mb-2"></div>
              
              {/* Invoice icon */}
              <div className="mt-2">
                <FileText size={20} className="text-blue-600" />
              </div>
            </div>
          </div>
          
          {/* Steam/cooking effect */}
          <div className="absolute top-20 left-0 right-0 flex justify-center">
            <div className="steam-1 w-3 h-3 bg-blue-100 rounded-full absolute animate-steam-1"></div>
            <div className="steam-2 w-4 h-4 bg-blue-100 rounded-full absolute animate-steam-2"></div>
            <div className="steam-3 w-3 h-3 bg-blue-100 rounded-full absolute animate-steam-3"></div>
          </div>
          
          {/* Sparkles effect */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="sparkle-1 w-2 h-2 bg-yellow-300 rounded-full absolute animate-sparkle-1"></div>
            <div className="sparkle-2 w-2 h-2 bg-yellow-300 rounded-full absolute animate-sparkle-2"></div>
            <div className="sparkle-3 w-2 h-2 bg-yellow-300 rounded-full absolute animate-sparkle-3"></div>
          </div>
        </div>
        
        <h2 className="text-2xl font-bold text-gray-800 mb-3">Your Invoice Is Being Crafted</h2>
        <p className="text-gray-600">Our chef is preparing your financial masterpiece!</p>
      </div>
      
      <style>{`
        @keyframes steam-1 {
          0%, 100% { transform: translateY(0) translateX(-10px) scale(0.8); opacity: 0; }
          50% { transform: translateY(-30px) translateX(5px) scale(1.2); opacity: 0.7; }
        }
        
        @keyframes steam-2 {
          0%, 100% { transform: translateY(0) translateX(10px) scale(0.9); opacity: 0; }
          50% { transform: translateY(-25px) translateX(-8px) scale(1.3); opacity: 0.6; }
        }
        
        @keyframes steam-3 {
          0%, 100% { transform: translateY(0) scale(0.7); opacity: 0; }
          50% { transform: translateY(-35px) scale(1.5); opacity: 0.8; }
        }
        
        @keyframes smile {
          0%, 100% { transform: scaleX(1); }
          50% { transform: scaleX(1.2); }
        }
        
        @keyframes sparkle-1 {
          0%, 100% { transform: translate(-20px, -20px) scale(0); opacity: 0; }
          50% { transform: translate(-25px, -25px) scale(1.5); opacity: 1; }
        }
        
        @keyframes sparkle-2 {
          0%, 100% { transform: translate(25px, 15px) scale(0); opacity: 0; }
          50% { transform: translate(20px, 10px) scale(1.5); opacity: 1; }
        }
        
        @keyframes sparkle-3 {
          0%, 100% { transform: translate(0, 20px) scale(0); opacity: 0; }
          50% { transform: translate(5px, 25px) scale(1.5); opacity: 1; }
        }
        
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.05); }
        }
        
        .animate-steam-1 {
          animation: steam-1 2.5s ease-in-out infinite;
        }
        
        .animate-steam-2 {
          animation: steam-2 3s ease-in-out infinite 0.5s;
        }
        
        .animate-steam-3 {
          animation: steam-3 2.8s ease-in-out infinite 1s;
        }
        
        .animate-smile {
          animation: smile 2s ease-in-out infinite;
        }
        
        .animate-spin-slow {
          animation: spin 12s linear infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
        
        .animate-sparkle-1 {
          animation: sparkle-1 3s ease-in-out infinite;
        }
        
        .animate-sparkle-2 {
          animation: sparkle-2 4s ease-in-out infinite 1s;
        }
        
        .animate-sparkle-3 {
          animation: sparkle-3 3.5s ease-in-out infinite 0.5s;
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default BillLoader;